import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { guideArticles } from '../data/guides';
import GuideCard from '../components/GuideCard';
import TourCard from '../components/TourCard';
import Hero from '../components/Hero';
import SmartImage from '../components/SmartImage';
import EmailCaptureForm from '../components/EmailCaptureForm';
import { useLanguage } from '../context/LanguageContext';
import { useConsent } from '../context/ConsentContext';
import { usePricing } from '../context/PricingContext';
import { usePageMeta, useStructuredData } from '../utils/meta';
import { trackViewItemList } from '../utils/analytics';
import { getTourCardImageProps, getTourFallbackImage, getTourImage } from '../utils/images';
import { buildSiteUrl } from '../utils/localizedRoutes';
import { businessInfo } from '../utils/businessInfo';

// Keep the tours with the strongest organic-search opportunity directly linked from home.
const topTourIds = [2, 13, 15, 16, 1, 4, 11, 6, 3, 8, 12, 5, 7];

export default function Home() {
  const { language, localizePath, t, translateValue } = useLanguage();
  const { canUse, grantCategory, openPreferences } = useConsent();
  const { tours } = usePricing();
  const operatorHighlights = t('home.operatorHighlights');
  const searchPages = t('home.searchPages');
  const proofCards = t('home.proofCards');
  const audienceCards = t('home.audienceCards');
  const faqItems = t('home.faqItems');
  const featuredGuides = guideArticles.slice(0, 3);
  const topTours = useMemo(
    () => topTourIds
      .map((id) => tours.find((tour) => tour.id === id))
      .filter(Boolean),
    [tours],
  );
  const privateTransportTour = tours.find((tour) => tour.id === 2);
  const partyBoatTour = tours.find((tour) => tour.id === 1);
  const keyWestTour = tours.find((tour) => tour.id === 11);
  const cityTour = tours.find((tour) => tour.id === 13);
  const serviceOffers = [
    {
      title: t('home.transportationTitle'),
      body: t('home.transportationBody'),
      meta: t('home.transportationMeta'),
      href: '/transportation',
      tour: privateTransportTour,
    },
    {
      title: t('home.waterTitle'),
      body: t('home.waterBody'),
      meta: t('home.waterMeta'),
      href: '/tours?cat=Water',
      tour: partyBoatTour,
    },
    {
      title: t('home.dayTripsTitle'),
      body: t('home.dayTripsBody'),
      meta: t('home.dayTripsMeta'),
      href: '/tours?cat=Adventure',
      tour: keyWestTour,
    },
    {
      title: t('home.cityTitle'),
      body: t('home.cityBody'),
      meta: t('home.cityMeta'),
      href: '/tours?cat=Cultural',
      tour: cityTour,
    },
    {
      title: t('home.luggageStorageTitle'),
      body: t('home.luggageStorageBody'),
      meta: t('home.luggageStorageMeta'),
      href: '/luggage-storage',
      tour: privateTransportTour,
      image: '/images/luggage-storage-items.webp',
    },
  ];

  usePageMeta({
    title: t('home.metaTitle'),
    description: t('home.metaDescription'),
    canonical: buildSiteUrl('/', language),
  });

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': ['TravelAgency', 'LocalBusiness'],
    name: businessInfo.name,
    url: buildSiteUrl('/', language),
    image: 'https://www.miamitoursandtravel.com/images/keywest-southernmost.webp',
    description: 'Miami Tours and Travel offers Miami tours, transportation, rentals, and day trips with Miami Beach pickup support for many bookings.',
    telephone: businessInfo.phone,
    address: businessInfo.address,
    sameAs: businessInfo.sameAs,
    areaServed: ['Miami', 'Miami Beach', 'Fort Lauderdale', 'Key West', 'Orlando', 'Bahamas'],
    priceRange: '$$',
    makesOffer: tours.slice(0, 12).map((tour) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: tour.name,
        description: tour.description,
      },
    })),
  }, 'home-structured-data');

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: businessInfo.name,
    alternateName: 'miamitoursandtravel.com',
    url: buildSiteUrl('/', language),
    inLanguage: language === 'es' ? 'es' : language === 'pt' ? 'pt-BR' : language === 'fr' ? 'fr' : 'en',
    publisher: {
      '@type': 'TravelAgency',
      name: businessInfo.name,
      url: buildSiteUrl('/', language),
      sameAs: businessInfo.sameAs,
    },
  }, 'home-website-structured-data');

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: Array.isArray(faqItems) ? faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })) : [],
  }, 'home-faq-structured-data');

  useEffect(() => {
    trackViewItemList({
      tours: topTours,
      listName: 'Home top experiences',
    });
  }, [topTours]);

  return (
    <div>
      <Hero />

      <section className="section home-proof-section">
        <div className="container">
          <div className="section-heading-row home-proof-header">
            <div>
              <span className="key-west-kicker">{t('home.whyChooseKicker')}</span>
              <h2>{t('home.whyChooseHeading')}</h2>
              <p>{t('home.whyChooseBody')}</p>
            </div>
            <div className="home-proof-actions">
              <a href="tel:+13054910131" className="btn-outline">{t('home.callSupport')}</a>
              <a href="https://g.page/r/CXXpjbmPOif2EBM/review" target="_blank" rel="noreferrer" className="btn-outline">
                {t('home.readReviews')}
              </a>
            </div>
          </div>
          {Array.isArray(proofCards) && (
            <div className="home-proof-grid">
              {proofCards.map((card) => (
                <article key={card.title} className="home-proof-card">
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="services-overview-section">
        <div className="container">
          <div className="services-overview-header">
            <span className="key-west-kicker">{t('home.offerKicker')}</span>
            <h2>{t('home.offerHeading')}</h2>
            <p>{t('home.offerBody')}</p>
          </div>
          <div className="services-overview-grid">
            {serviceOffers.map((offer) => (
              <Link key={offer.title} to={localizePath(offer.href)} className="service-offer-card">
                {(() => {
                  const responsiveImageProps = getTourCardImageProps(offer.tour);

                  return (
                    <SmartImage
                      src={offer.image || responsiveImageProps.src || getTourImage(offer.tour)}
                      srcSet={offer.image ? undefined : responsiveImageProps.srcSet}
                      sizes="(max-width: 767px) 92vw, (max-width: 1200px) 48vw, 236px"
                      fallbackSrc={getTourFallbackImage(offer.tour)}
                      alt={t('home.serviceImageAlt', { title: offer.title })}
                      className="service-offer-image"
                    />
                  );
                })()}
                <span className="service-offer-meta">{offer.meta}</span>
                <h3>{offer.title}</h3>
                <p>{offer.body}</p>
                <span className="service-offer-link">{t('home.explore')}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section home-video-section">
        <div className="container home-video-layout">
          <div className="home-video-copy">
            <span className="key-west-kicker">{t('home.videoKicker')}</span>
            <h2>{t('home.videoHeading')}</h2>
            <p>{t('home.videoBody')}</p>
            <Link to={localizePath('/tours')} className="hero-btn">{t('home.videoCta')}</Link>
          </div>
          <div className="home-video-frame">
            {canUse('externalMedia') ? (
              <iframe
                title={t('home.videoTitle')}
                src="https://www.youtube-nocookie.com/embed/argaE0wbAFE"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <div className="home-video-placeholder">
                <span>▶</span>
                <h3>{t('home.videoBlockedHeading')}</h3>
                <p>{t('home.videoBlockedBody')}</p>
                <div className="home-video-actions">
                  <button type="button" className="book-btn" onClick={() => grantCategory('externalMedia')}>
                    {t('home.videoLoad')}
                  </button>
                  <button type="button" className="btn-outline" onClick={openPreferences}>
                    {t('cookieConsent.chooseSettings')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading-row">
            <h2>{t('home.topHeading')}</h2>
            <span className="mobile-swipe-hint">{t('home.swipeHint')}</span>
          </div>
          <div className="tours-grid home-top-tours-grid">
            {topTours.map((tour, index) => (
              <TourCard
                key={tour.id}
                tour={tour}
                listName="Home top experiences"
                itemIndex={index}
              />
            ))}
          </div>
          <div style={{textAlign:'center', marginTop:'2rem'}}>
            <Link to={localizePath('/tours')} className="btn-outline">{t('home.seeAll')}</Link>
          </div>
        </div>
      </section>

      <section className="categories-section">
        <div className="container">
          <h2>{t('home.browseHeading')}</h2>
          <div className="category-pills">
            {['Water', 'Cultural', 'Adventure', 'Food', 'Nightlife'].map(c => (
              <Link key={c} to={localizePath(`/tours?cat=${c}`)} className="category-pill">
                {translateValue('categoryLabels', c)}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section home-audience-section">
        <div className="container">
          <div className="services-overview-header">
            <span className="key-west-kicker">{t('home.audienceKicker')}</span>
            <h2>{t('home.audienceHeading')}</h2>
            <p>{t('home.audienceBody')}</p>
          </div>
          {Array.isArray(audienceCards) && (
            <div className="home-audience-grid">
              {audienceCards.map((card) => (
                <article key={card.title} className="home-audience-card">
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section home-search-pages-section">
        <div className="container">
          <div className="home-guide-header">
            <span className="key-west-kicker">{t('home.searchPagesKicker')}</span>
            <h2>{t('home.searchPagesHeading')}</h2>
            <p>{t('home.searchPagesBody')}</p>
          </div>
          <div className="home-guide-grid">
            {searchPages.map((page) => (
              <Link key={page.href} to={localizePath(page.href)} className="home-guide-card seo-link-card">
                <h3>{page.title}</h3>
                <p>{page.body}</p>
                <span className="service-offer-link">{t('home.explore')}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="featured-destination">
            <div className="featured-destination-copy">
              <span className="key-west-kicker">{t('home.operatorKicker')}</span>
              <h2>{t('home.operatorHeading')}</h2>
              <p>{t('home.operatorBody')}</p>
              {Array.isArray(operatorHighlights) && (
                <ul className="featured-destination-list">
                  {operatorHighlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              )}
              <div className="featured-destination-actions">
                <Link to={localizePath('/tours')} className="hero-btn">{t('home.operatorPrimaryCta')}</Link>
                <a
                  href="https://g.page/r/CXXpjbmPOif2EBM/review"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline"
                >
                  {t('home.readReviews')}
                </a>
              </div>
            </div>
            <img
              src="/images/private-transport-van.webp"
              alt={t('home.operatorImageAlt')}
              className="featured-destination-image"
            />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <EmailCaptureForm variant="home" />
        </div>
      </section>

      <section className="section home-guides-preview">
        <div className="container">
          <div className="section-heading-row home-guides-preview-header">
            <div>
              <span className="key-west-kicker">{t('home.guidesPreviewKicker')}</span>
              <h2>{t('home.guidesPreviewHeading')}</h2>
              <p>{t('home.guidesPreviewBody')}</p>
            </div>
            <Link to={localizePath('/guides')} className="btn-outline">{t('guides.viewAll')}</Link>
          </div>
          <div className="guides-grid guides-grid--compact">
            {featuredGuides.map((guide) => (
              <GuideCard key={guide.slug} guide={guide} />
            ))}
          </div>
        </div>
      </section>

      <section className="section home-faq-section">
        <div className="container home-faq-container">
          <span className="key-west-kicker">{t('home.whyChooseKicker')}</span>
          <h2>{t('home.faqHeading')}</h2>
          <p className="home-faq-intro">{t('home.faqIntro')}</p>
          {Array.isArray(faqItems) && (
            <div className="home-faq-list">
              {faqItems.map((item) => (
                <details key={item.question} className="home-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust bar */}
      <section className="trust-bar">
        <div className="trust-item"><span>🚐</span><p>{t('home.trustTransport')}</p></div>
        <div className="trust-item"><span>🌴</span><p>{t('home.trustTrips')}</p></div>
        <div className="trust-item"><span>📍</span><p>{t('home.trustPickup')}</p></div>
        <div className="trust-item"><span>🤝</span><p>{t('home.trustPrivate')}</p></div>
      </section>
    </div>
  );
}
